import type { Attachment, ChatMode, ChatStreamEvent } from '@shared/types'

export interface StreamChatBody {
  chatId?: string
  message: string
  attachments?: Attachment[]
  liveSearch?: boolean
  skillIds?: string[]
  mcpIds?: string[]
  mode?: ChatMode
  /** Model id from shared/models.ts (grok-4.3 / grok-4.6). */
  model?: string
  /** Chat's Trading switch (admin) — attaches the trade-worker tools. */
  trading?: boolean
}

/** Parse an SSE response body, invoking onEvent for each `data:` frame. */
async function consumeSSE(body: ReadableStream<Uint8Array>, onEvent: (e: ChatStreamEvent) => void): Promise<void> {
  const reader = body.getReader()
  const decoder = new TextDecoder()
  let buf = ''
  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      buf += decoder.decode(value, { stream: true })

      let sep: number
      while ((sep = buf.indexOf('\n\n')) !== -1) {
        const frame = buf.slice(0, sep)
        buf = buf.slice(sep + 2)
        const data = frame
          .split('\n')
          .filter((l) => l.startsWith('data:'))
          .map((l) => l.slice(5).replace(/^ /, ''))
          .join('\n')
        if (!data) continue
        try {
          onEvent(JSON.parse(data) as ChatStreamEvent)
        } catch {
          /* ignore malformed frame */
        }
      }
    }
  } catch (err) {
    if ((err as Error)?.name !== 'AbortError') {
      onEvent({ type: 'error', message: (err as Error)?.message ?? 'Stream error' })
    }
  } finally {
    reader.releaseLock()
  }
}

/**
 * POST /api/chat and consume the SSE stream, invoking onEvent for each
 * ChatStreamEvent. Resolves when the stream closes.
 */
export async function streamChat(
  body: StreamChatBody,
  onEvent: (e: ChatStreamEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  let res: Response
  try {
    res = await fetch('/api/chat', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
      signal,
    })
  } catch (err) {
    if ((err as Error)?.name === 'AbortError') return
    onEvent({ type: 'error', message: (err as Error)?.message ?? 'Network error' })
    onEvent({ type: 'done' })
    return
  }

  if (!res.ok || !res.body) {
    onEvent({ type: 'error', message: `Server error ${res.status}` })
    onEvent({ type: 'done' })
    return
  }
  await consumeSSE(res.body, onEvent)
}

/**
 * Re-attach to a generation that's still running server-side for this chat (after a
 * reconnect/reload). Replays everything so far, then streams the rest. If nothing is
 * running, the server closes the stream immediately (a single 'done'). Stays SILENT on
 * connection errors — resume is best-effort.
 */
export async function streamResume(
  chatId: string,
  onEvent: (e: ChatStreamEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  let res: Response
  try {
    res = await fetch(`/api/chat/${chatId}/stream`, { signal })
  } catch {
    onEvent({ type: 'done' })
    return
  }
  if (!res.ok || !res.body) {
    onEvent({ type: 'done' })
    return
  }
  await consumeSSE(res.body, onEvent)
}
